import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { bookingSettingsApi } from '../api/bookingSettingsApi';
import { emailConnectionApi } from '../api/emailApi';
import { CreateBookingSettingsDto } from '../types/booking';

const QUERY_OPTIONS = {
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
  retry: false,
  staleTime: 60 * 1000,
} as const;

const QUERY_KEYS = {
  settings: ['booking-settings'],
  calendarConnection: ['calendar-connection'],
};

export const useBookingSettings = (
  enabled = true
) =>
  useQuery({
    queryKey: QUERY_KEYS.settings,
    queryFn: () =>
      bookingSettingsApi
        .getSettings()
        .then((res) => res.data),
    ...QUERY_OPTIONS,
    enabled,
  });

export const useCalendarConnection = () =>
  useQuery({
    queryKey: QUERY_KEYS.calendarConnection,
    queryFn: () => bookingSettingsApi.getCalendarConnection().then((res) => res.data),
    ...QUERY_OPTIONS,
  });

export const useUpdateBookingSettings = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateBookingSettingsDto) => bookingSettingsApi.saveSettings(data).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.settings });
      toast.success('Booking settings saved successfully');
    },
    onError: () => toast.error('Failed to save booking settings'),
  });
};

export const useDisconnectEmailConnection = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      await emailConnectionApi.disconnect();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.calendarConnection });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.settings });
      toast.success('Calendar disconnected successfully');
    },
    onError: () => toast.error('Failed to disconnect calendar connection'),
  });
};

export const useConnectGoogle = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await emailConnectionApi.connectGoogle();
      const authUrl = response.authUrl ?? '';
      if (!authUrl) {
        throw new Error('No Google auth URL returned');
      }
      return authUrl;
    },
    onSuccess: (authUrl) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.calendarConnection });
      window.location.href = authUrl;
    },
    onError: () => toast.error('Failed to connect Google Calendar. Please try again.'),
  });
};

export const useConnectMicrosoft = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await emailConnectionApi.connectMicrosoft();
      const authUrl = response.authUrl ?? '';
      if (!authUrl) {
        throw new Error('No Microsoft auth URL returned');
      }
      return authUrl;
    },
    onSuccess: (authUrl) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.calendarConnection });
      window.location.href = authUrl;
    },
    onError: () => toast.error('Failed to connect Microsoft Calendar. Please try again.'),
  });
};

